import React from 'react';
import { Kpi, Card, Donut, Area, Line, Meter } from './Charts';

// Reports / Overview tab — executive summary. Consumes the live dataset (D)
// returned by /api/dashboard/reports.

const qPill = (q) => {
    if (q >= 96) return { background: 'var(--green-soft)', color: 'var(--green)' };
    if (q >= 94) return { background: 'var(--yellow-soft)', color: '#a87a14' };
    return { background: '#fbe1e1', color: '#b53e3e' };
};

const OverviewTab = ({ D, range, filters }) => {
    const k = D.KPIS[range];
    const sla = D.SLA[range];
    const flow = D.FLOW[range];
    const langFilter = (filters && filters.lang) || [];
    let langs = D.LANGS.slice(0, 6);
    if (langFilter.length) langs = D.LANGS.filter((l) => langFilter.includes(l[1]));
    const maxW = Math.max(1, ...langs.map((l) => l[2]));
    const portfolio = D.PORTFOLIO || [];
    const totalProj = portfolio.reduce((s, p) => s + p[1], 0);

    return (
        <>
            <div className="rpt-kpis five">
                <Kpi label="Words translated" value={k.words.value} delta={k.words.delta} arrow={k.words.arrow} tone={k.words.tone} spark={D.SPARK.words} color="var(--accent)" />
                <Kpi label="On-time delivery" value={k.ontime.value} delta={k.ontime.delta} arrow={k.ontime.arrow} tone={k.ontime.tone} spark={D.SPARK.ontime} color="var(--green)" />
                <Kpi label="Quality" unit="LQA" value={k.quality.value} delta={k.quality.delta} arrow={k.quality.arrow} tone={k.quality.tone} spark={D.SPARK.quality} color="var(--indigo)" />
                <Kpi label="Avg turnaround" value={k.turn.value} delta={k.turn.delta} arrow={k.turn.arrow} tone={k.turn.tone} spark={D.SPARK.turn} color="var(--green)" />
                <Kpi label="MT leverage" value={k.mt.value} delta={k.mt.delta} arrow={k.mt.arrow} tone={k.mt.tone} spark={D.SPARK.mt} color="var(--accent)" />
            </div>

            <div className="rpt-row a">
                <Card title="Throughput" sub="words translated per period"
                    legend={[{ label: 'Human', color: 'var(--accent)' }, { label: 'MT post-edit', color: 'var(--indigo)' }, { label: 'In review', color: 'var(--green)' }]}>
                    <div className="rpt-plot">
                        <Area labels={flow.labels} unit="k" series={[
                            { name: 'Human', color: 'var(--accent)', data: flow.human },
                            { name: 'MT post-edit', color: 'var(--indigo)', data: flow.mt },
                            { name: 'In review', color: 'var(--green)', data: flow.review, fill: 0.6 },
                        ]} />
                    </div>
                </Card>
                <Card title="On-time delivery">
                    <div className="rpt-donut-wrap">
                        <Donut segments={[{ value: sla.pct, color: 'var(--green)', label: 'On time' }]} center={`${sla.pct}%`} sub="on time" />
                        <div className="rpt-donut-meta">
                            <div className="rows">
                                <div><span className="dot green" />On time<b>{sla.onTime}</b></div>
                                <div><span className="dot" style={{ background: 'var(--yellow)' }} />At risk<b>{sla.atRisk}</b></div>
                                <div><span className="dot" style={{ background: 'var(--accent)' }} />Late<b>{sla.late}</b></div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            <div className="rpt-row b">
                <Card title="By language" sub="words · quality · on-time">
                    <div className="rpt-tbl lang">
                        <div className="rpt-tr head"><span>Language</span><span className="grow2">Volume</span><span className="num">Words</span><span className="num">LQA</span><span className="num">On-time</span></div>
                        {langs.length === 0 ? (
                            <div className="rpt-empty">No language volume in this range yet.</div>
                        ) : langs.map(([code, name, words, qual, ontime]) => (
                            <div className="rpt-tr" key={code}>
                                <span className="who"><span className="rpt-code">{code}</span>{name}</span>
                                <span className="grow2"><Meter value={words} max={maxW} /></span>
                                <span className="num b">{words}k</span>
                                <span className="num"><span className="q-pill" style={qPill(qual)}>{qual}</span></span>
                                <span className="num">{ontime}%</span>
                            </div>
                        ))}
                    </div>
                </Card>
                <Card title="Quality trend" sub="avg LQA score / week">
                    <div className="rpt-plot sm">
                        <Line data={D.QUALITY_TREND} min={94} max={97} ticks={[94, 95, 96, 97]} target={95} targetLabel="target 95" color="var(--indigo)" />
                    </div>
                </Card>
            </div>

            <Card title="Project portfolio" sub={`${totalProj} active projects`}>
                <div className="rpt-pad">
                    <div className="rpt-portfolio">
                        <div className="rpt-portfolio-bar tall">
                            {portfolio.map(([label, count, color]) => (
                                count > 0 ? <span key={label} style={{ width: `${(count / (totalProj || 1)) * 100}%`, background: color }} title={`${label}: ${count}`} /> : null
                            ))}
                        </div>
                        <div className="rpt-portfolio-legend wide">
                            {portfolio.map(([label, count, color]) => (
                                <div key={label}><span className="dot" style={{ background: color }} />{label}<b>{count}</b></div>
                            ))}
                        </div>
                    </div>
                </div>
            </Card>
        </>
    );
};

export default OverviewTab;
